<template>
  <div class="gauge">
    <svg
      :width="size"
      :height="size / 2 + 80"
      :viewBox="`0 0 ${size} ${size / 2 + 40}`"
      style="overflow: visible"
    >
      <path
        v-for="(seg, i) in segments"
        :key="i"
        :d="seg.path"
        :stroke="seg.color"
        :stroke-width="arcWidth"
        fill="none"
        stroke-linecap="butt"
      />

      <g v-for="(pct, i) in percentTicks" :key="i">
        <text
          :x="tickCoords[i].x + 2"
          :y="tickCoords[i].y"
          text-anchor="middle"
          font-size="10"
          fill="#111"
        >
          {{ pct }}%
        </text>
        <text
          v-if="i == 0 || i + 1 == tickValues.length"
          :x="tickCoords2[i].x"
          :y="tickCoords2[i].y + 10"
          text-anchor="middle"
          font-size="10"
          fill="#333"
        >
          € {{ tickValues[i] }}
        </text>
      </g>
      <g :transform="`rotate(${needleAngle}, ${centerX}, ${centerY})`">
        <path :d="needlePath" fill="#333" />
      </g>

      <circle
        :cx="centerX"
        :cy="centerY"
        :r="pivotR"
        fill="#fff"
        stroke="#333"
        stroke-width="2"
      />
    </svg>

    <div class="gauge-label">€ {{ consumed }} Consumed Spend Cap</div>
  </div>
</template>

<script>
export default {
  name: "Gauge",
  props: {
    capLimit: { type: Number, required: true }, // e.g. 20
    consumed: { type: Number, required: true }, // e.g. 15
    size: { type: Number, default: 200 }, // overall SVG width
    arcWidth: { type: Number, default: 14 }, // thickness of the arcs
  },
  computed: {
    centerX() {
      return this.size / 2;
    },
    centerY() {
      return this.size / 2;
    },

    radius() {
      return this.size / 2 - this.arcWidth / 2 - 2;
    },

    segments() {
      const cuts = [0.5, 0.75, 1];
      const colors = ["#4caf50", "#ffb74d", "#e57373"];
      let last = 0;
      return cuts.map((t, i) => {
        const startA = 180 + last * 180;
        const endA = 180 + t * 180;
        last = t;
        return {
          color: colors[i],
          path: this.describeArc(
            this.centerX,
            this.centerY,
            this.radius,
            startA,
            endA
          ),
        };
      });
    },

    percentTicks() {
      return [0, 25, 50, 75, 100];
    },
    tickValues() {
      return this.percentTicks.map((p) =>
        Math.round(((p / 100) * this.capLimit) / 10000)
      );
    },
    tickCoords() {
      const r = this.radius - this.arcWidth / 2 + 25;
      return this.percentTicks.map((p) => {
        const θ = ((180 - (p / 100) * 180) * Math.PI) / 180;
        return {
          x: this.centerX + r * Math.cos(θ),
          y: this.centerY - r * Math.sin(θ),
        };
      });
    },
    tickCoords2() {
      const r = this.radius - this.arcWidth / 2 + 8;
      return this.percentTicks.map((p) => {
        const θ = ((180 - (p / 100) * 180) * Math.PI) / 180;
        return {
          x: this.centerX + r * Math.cos(θ),
          y: this.centerY - r * Math.sin(θ),
        };
      });
    },

    percent() {
      return Math.min(1, this.consumed / this.capLimit / 10000);
    },

    needleAngle() {
      return 180 + this.percent * 180;
    },
    needleLength() {
      return this.radius;
    },
    needleBase() {
      return this.arcWidth / 2;
    },
    needlePath() {
      const cx = this.centerX,
        cy = this.centerY,
        b = this.needleBase,
        L = this.needleLength;
      return `M ${cx},${cy - b} L ${cx},${cy + b} L ${cx + L},${cy} Z`;
    },

    pivotR() {
      return this.arcWidth / 2;
    },
  },

  methods: {
    polarToCartesian(cx, cy, r, angleDeg) {
      const rad = (angleDeg * Math.PI) / 180;
      return {
        x: cx + r * Math.cos(rad),
        y: cy + r * Math.sin(rad),
      };
    },

    describeArc(cx, cy, r, startAngle, endAngle) {
      const start = this.polarToCartesian(cx, cy, r, startAngle);
      const end = this.polarToCartesian(cx, cy, r, endAngle);
      return ["M", start.x, start.y, "A", r, r, 0, 0, 1, end.x, end.y].join(
        " "
      );
    },
  },
};
</script>

<style scoped>
.gauge {
  display: inline-block;
  text-align: center;
  overflow: none;
}
.gauge-label {
  margin-top: -50px;
  font-size: 14px;
  font-weight: 500;
  color: #444;
}
</style>
